import type { DieSpellDef, DieSpellId, DieSpellPool } from '../core/types';
import { BALANCE } from './balance';

/**
 * Sorts de **dé** — COMMENTS v0.4.1 §1.4 et arbitrages A3 / A4 / A9.
 *
 * Un sort de dé est attribué au dé **à sa création** (Gachapon, forge de debug,
 * Dé de l'aïeul) et le suit toute sa vie. Il s'évalue sur les faces des dés
 * Maîtres du lancer, comme les sorts d'emplacement.
 *
 * Contrairement aux sorts d'emplacement, **tous** les sorts de dé distincts qui
 * correspondent se déclenchent ensemble (A9) : les multiplicateurs se
 * multiplient, les secondes et les multiplicateurs d'or s'additionnent /
 * se multiplient. Deux dés portant le même sort ne le déclenchent qu'une fois.
 *
 * v0.7.0 (COMMENTS v0.6.0 §1.2.2) : *666* devient un sort de **dé** (l'Enfer,
 * ×1,5 jusqu'à la fin du combat) et *Premiers* le rejoint ; tous deux sont
 * **rares**. Leur effet n'est pas un multiplicateur de lancer : il est porté par
 * l'état de combat (`state.combat.inferno` / `state.combat.primesQueue`).
 *
 * Répartition par rareté (A3) : Commun / Inhabituel / Rare → 1 sort,
 * Épique / Légendaire → 2 sorts distincts. Un sort **rare** (`rare: true`)
 * n'apparaît qu'à partir de Rare, avec 30 % de chance par sort ; le Légendaire
 * en a un garanti.
 */
export const DIE_SPELLS: DieSpellDef[] = [
  {
    // v0.49.0 (COMMENTS v0.48.1 §1.3.1, A98-2) : plus de secondes ; les deux
    // dés déclencheurs sont relancés au lancer suivant (`core/combat.ts`).
    id: 'fortyTwo',
    name: '42',
    rare: false,
    multiplier: 1,
    bonusSeconds: 0,
    goldMult: 1,
    condition: 'Les faces 4 et 2 sont présentes',
    description: 'Au prochain lancer, les deux dés qui ont fait le 4 et le 2 sont lancés une fois de plus.',
  },
  {
    id: 'parity',
    name: 'Parité',
    rare: false,
    multiplier: BALANCE.spellParityMultiplier,
    bonusSeconds: 0,
    goldMult: 1,
    condition: 'Toutes les faces sont paires, ou toutes impaires',
    description: 'Si toutes les faces sont paires ou impaires, multiplie les dégâts par 1,5.',
  },
  {
    id: 'pi',
    name: 'Pi',
    rare: true,
    multiplier: BALANCE.spellPiMultiplier,
    bonusSeconds: 0,
    goldMult: 1,
    condition: 'Les faces 3, 1 et 4 sont présentes',
    description: 'Multiplie les dégâts du lancer par 3,14.',
  },
  {
    id: 'maxroll',
    name: 'Maxroll',
    rare: true,
    multiplier: BALANCE.spellMaxrollMultiplier,
    bonusSeconds: 0,
    goldMult: 1,
    condition: 'Tous les dés Maîtres affichent leur face maximale',
    description:
      'Si tous vos dés affichent une face maximale, multiplie les dégâts du lancer par 1,75. ' +
      'Cet effet est doublé par emplacement supplémentaire par rapport à 2.',
  },
  {
    id: 'goldenRatio',
    name: 'Nombre d’or',
    rare: true,
    multiplier: 1,
    bonusSeconds: 0,
    goldMult: BALANCE.spellGoldenRatioGoldMult,
    condition: 'Les faces 1, 6, 1 et 8 sont présentes',
    description: 'Multiplie les gains en or du monstre à sa mort par 1,61.',
  },
  {
    id: 'sixes',
    name: '666',
    rare: true,
    multiplier: 1,
    bonusSeconds: 0,
    goldMult: 1,
    condition: 'Trois dés Maîtres affichent un 6',
    description:
      'Le sol s’embrase jusqu’à la fin du combat en cours : tous les lancers voient leurs ' +
      'dégâts multipliés par 1,5. L’effet ne se cumule pas.',
  },
  {
    id: 'primes',
    name: 'Premiers',
    rare: true,
    multiplier: 1,
    bonusSeconds: 0,
    goldMult: 1,
    condition:
      'Un nombre premier de dés Maîtres affiche une face première (2, 3, 5, 7, 11, 13, 17, 19, 23 … 97)',
    description:
      'Multiplie les dégâts des 3 prochains lancers par 2, 3 puis 5. Le lancer déclencheur ' +
      'n’est pas concerné et un redéclenchement pendant la séquence est ignoré.',
  },
  // ── v0.49.0 — COMMENTS v0.48.1 §1.3.2 (A98-4 : pas de rareté Inhabituel,
  // Vingt-dicte, Null et Tempête communs, Rouleau Compresseur rare). Ils
  // poussent vers les d12 et les d20 : `minSides`.
  {
    id: 'twentyDict',
    name: 'Vingt-dicte',
    rare: false,
    minSides: BALANCE.spellD12MinSides,
    multiplier: 1,
    bonusSeconds: 0,
    goldMult: 1,
    condition: `Au moins ${BALANCE.spellTwentyDictCount} faces Maîtres strictement supérieures à ${BALANCE.spellTwentyDictFace}`,
    description: 'Ce lancer lance le double de dés Basiques (contenant et tractopelle compris).',
  },
  {
    id: 'nullMark',
    name: 'Null',
    rare: false,
    minSides: BALANCE.spellD12MinSides,
    multiplier: 1,
    bonusSeconds: 0,
    goldMult: 1,
    condition: 'Aucune face Maître en double (au moins deux dés)',
    // v0.50.0 (COMMENTS v0.49.1 §1.2) : texte du GD — les lancers marqués
    // frappent **et** s'accumulent (A98-3 les retenait).
    description:
      'Au premier déclenchement, pose une marque sur le monstre. Les prochains lancers ' +
      'infligent leurs dégâts au monstre et s’accumulent dans la marque. Au second ' +
      'déclenchement, libère les dégâts stockés dans la marque.',
  },
  {
    id: 'storm',
    name: 'Tempête',
    rare: false,
    minSides: BALANCE.spellD12MinSides,
    multiplier: 1,
    bonusSeconds: 0,
    goldMult: 1,
    condition: `${BALANCE.spellStormCount} faces Maîtres strictement supérieures à ${BALANCE.spellStormFace}`,
    description:
      `Une tempête fait rage pendant ${BALANCE.spellStormDuration} secondes : votre temps de retour et ` +
      `celui du Larbin sont réduits de ${Math.round(BALANCE.spellStormReduction * 100)} %. La relancer ` +
      'rafraîchit sa durée.',
  },
  {
    id: 'steamroller',
    name: 'Rouleau Compresseur',
    rare: true,
    minSides: BALANCE.spellSteamrollerMinSides,
    multiplier: 1,
    dynamic: true,
    bonusSeconds: 0,
    goldMult: 1,
    condition:
      'La plus haute face Maître du lancer est au moins égale à celle du lancer précédent du combat',
    description:
      'Multiplie les dégâts du lancer par le produit de sa plus petite et de sa plus haute face Maître.',
  },
];

export const DIE_SPELL_BY_ID: Record<DieSpellId, DieSpellDef> = Object.fromEntries(
  DIE_SPELLS.map((s) => [s.id, s]),
) as Record<DieSpellId, DieSpellDef>;

/**
 * Sorts d'un pool donné : `rare` (Pi, Maxroll, Nombre d'or…) ou `common`.
 * `sides` (v0.49.0, A98-4) : seulement les sorts qu'un dé à `sides` faces peut
 * recevoir (`minSides`) ; sans `sides`, tous.
 */
export function dieSpellsOfPool(pool: DieSpellPool, sides?: number): DieSpellId[] {
  return DIE_SPELLS.filter(
    (s) =>
      (s.rare ? 'rare' : 'common') === pool &&
      (sides === undefined || s.minSides === undefined || sides >= s.minSides),
  ).map((s) => s.id);
}

/**
 * Nombre de sorts portés par un dé selon sa rareté — A3.
 * Prismatique est hors jeu (poids nul) : il suit le Légendaire.
 */
export const DIE_SPELL_COUNT: Record<string, number> = {
  common: 1,
  uncommon: 1,
  rare: 1,
  epic: 2,
  legendary: 2,
  prismatic: 2,
};
